import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
/** Read the chrome's labels from the render context (falls back through `t()` like every other section). */
export function videoControlLabels(context) {
    return {
        play: t(context, 'a11y.play_video'),
        pause: t(context, 'a11y.pause_video'),
        mute: t(context, 'a11y.mute_video'),
        unmute: t(context, 'a11y.unmute_video'),
        fullscreen: t(context, 'a11y.fullscreen'),
    };
}
/** Base class of every disc. Kept short — it is printed on every video on every page. */
export const VC = 'aqx-vc';
/**
 * The chrome's stylesheet, scoped to one section's class.
 *
 * `scope` is the section's generated class (`.aqx-video-x1y2`), so two sections on the same page never
 * fight over the disc size. Pass `''` for an unscoped, page-wide block (the storefront's global sheet).
 */
export function videoControlsCss(scope) {
    const S = scope ? `${scope} ` : '';
    const B = `${S}.${VC}`;
    /**
     * L'ancre des règles d'état — l'élément qui PORTE `data-playing` / `data-muted`.
     *
     * Deux familles l'écrivent. `videoInit` le pose sur le conteneur qu'il a adopté (`[data-aqx-video]`) ;
     * les sections à moteur PROPRE — les onglets (`contentTabsInit`), qui gardent chaque panneau dans le
     * DOM et ne veulent surtout pas d'un second moteur sur le même fichier — le posent sur un élément
     * marqué `[data-aqx-video-state]`. Sans cette deuxième ancre, leur disque était peint SANS AUCUNE
     * icône : les deux `display:block` ci-dessous ne pouvaient jamais s'appliquer.
     */
    const STATE = `:is([data-aqx-video],[data-aqx-video-state])`;
    return [
        // ── the disc ──────────────────────────────────────────────────────────────────────────────────────
        `${B}{--aqx-vc-size:56px;` +
            `--aqx-vc-bg:color-mix(in srgb,var(--aqx-scheme-bg,var(--aqx-theme-bg,#fff)) 82%,transparent);` +
            `--aqx-vc-fg:var(--aqx-scheme-text,var(--aqx-theme-ink,#1a1a1a));` +
            `--aqx-vc-radius:999px;` +
            `position:absolute;z-index:2;display:inline-flex;align-items:center;justify-content:center;` +
            `inline-size:var(--aqx-vc-size);block-size:var(--aqx-vc-size);padding:0;margin:0;` +
            `border:1px solid color-mix(in srgb,var(--aqx-vc-fg) 12%,transparent);` +
            `border-radius:var(--aqx-vc-radius);background:var(--aqx-vc-bg);color:var(--aqx-vc-fg);` +
            // The disc sits ON a photo, which can be any colour. A blur + a soft shadow is what keeps it
            // readable over a bright frame without painting an opaque block over the seller's image.
            `-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);` +
            `box-shadow:0 2px 10px color-mix(in srgb,#000 18%,transparent);` +
            `cursor:pointer;pointer-events:auto;line-height:0;` +
            `transition:transform .18s ease,opacity .18s ease,background-color .18s ease,color .18s ease}`,
        `${B}:hover{transform:scale(1.08)}`,
        `${B}:active{transform:scale(.96)}`,
        // Keyboard focus: a two-tone ring so it reads on a dark frame AND a light one.
        `${B}:focus{outline:0}`,
        `${B}:focus-visible{outline:0;box-shadow:0 0 0 3px var(--aqx-vc-bg),0 0 0 5px var(--aqx-vc-fg)}`,
        // NO `display` HERE, and that is the whole point (caught in a browser, 18/09/2026). This rule is
        // `.section .aqx-vc svg` — two classes and a type — while the icon-visibility rule below is
        // `.section .aqx-vc__icon`, two classes. A `display:block` written here therefore OUTRANKED the
        // `display:none` that hides the icon of the pair that is not current, and every disc painted BOTH
        // its play triangle and its pause bars, overlapping. Sizing belongs here; visibility belongs to the
        // state rules alone.
        `${B} svg{inline-size:calc(var(--aqx-vc-size) * .38);block-size:calc(var(--aqx-vc-size) * .38);fill:currentColor}`,
        // A play TRIANGLE reads off-centre inside a circle unless it is nudged: its visual mass sits left of
        // its bounding box. Shrine does the same 2px shift on its own poster button. The pause bars are
        // symmetrical and must NOT be shifted, or the disc jitters sideways on every toggle.
        `${B}.${VC}--play .${VC}__icon--play{margin-inline-start:calc(var(--aqx-vc-size) * .03)}`,
        // ── placement ─────────────────────────────────────────────────────────────────────────────────────
        `${B}.${VC}--center{inset-block-start:50%;inset-inline-start:50%;transform:translate(-50%,-50%)}`,
        `${B}.${VC}--center:hover{transform:translate(-50%,-50%) scale(1.08)}`,
        `${B}.${VC}--center:active{transform:translate(-50%,-50%) scale(.96)}`,
        `${B}.${VC}--bottom-start{inset-block-end:12px;inset-inline-start:12px}`,
        `${B}.${VC}--bottom-end{inset-block-end:12px;inset-inline-end:12px}`,
        `${B}.${VC}--top-end{inset-block-start:12px;inset-inline-end:12px}`,
        // The corner buttons are secondary chrome, so they are smaller than the play disc.
        `${B}.${VC}--sound,${B}.${VC}--fs{--aqx-vc-size:38px}`,
        `${B}.${VC}--sound svg,${B}.${VC}--fs svg{inline-size:calc(var(--aqx-vc-size) * .45);block-size:calc(var(--aqx-vc-size) * .45)}`,
        // ── which icon of the pair shows ──────────────────────────────────────────────────────────────────
        // The engine flips ONE attribute on the container; CSS does the rest, so a toggle never touches the
        // button's children (that is what killed the previous, emoji-based chrome).
        `${S}.${VC}__icon{display:none}`,
        // EVERY state selector is ANCHORED ON THE ELEMENT THAT CARRIES THE STATE, and the `:not()` ones are
        // why (caught in a
        // browser, 18/09/2026). Written as `${'${scope}'} :not([data-playing='true']) …` the `:not()` is a
        // DESCENDANT step, so it matches any ancestor without the attribute — the section wrapper, the page
        // width div, anything — and the rule fired even while the clip was running. Both icons were painted,
        // one on top of the other. Anchoring it on the element that actually carries the state is the fix.
        `${S}${STATE}[data-playing='true'] .${VC}--play .${VC}__icon--pause{display:block}`,
        // `:not([data-playing='true'])` and not `[data-playing='false']`: before the engine runs — SSR, a
        // no-JS visitor, the builder's first paint — the attribute is absent, and the button must still show
        // something. It shows the play triangle, which is what a paused video means.
        `${S}${STATE}:not([data-playing='true']) .${VC}--play .${VC}__icon--play{display:block}`,
        `${S}${STATE}[data-muted='false'] .${VC}--sound .${VC}__icon--unmuted{display:block}`,
        `${S}${STATE}:not([data-muted='false']) .${VC}--sound .${VC}__icon--muted{display:block}`,
        `${S}.${VC}--fs .${VC}__icon--fs{display:block}`,
        // A decorative marker (the slider's poster-only slide) is a lone icon in a span, with no state and no
        // pair, so it gets its `display` from here rather than from a state rule that will never fire.
        `${S}span > svg.${VC}__icon,${S}span.${VC} > svg{display:block}`,
        // ── the auto-fade, and why it exists ──────────────────────────────────────────────────────────────
        // Every video component now STARTS on its own (founder, 18/09/2026), so the play disc is no longer a
        // thing the visitor must find — it is a thing they must be able to find AGAIN. Parked over a clip that
        // is already running it would be a permanent stamp on the seller's picture. So while the clip plays it
        // fades out, and it comes back on hover, on keyboard focus, and on touch (`:focus-within` covers the
        // tap, which focuses the button on every mobile browser).
        `${S}[data-playing='true'] .${VC}--play{opacity:0}`,
        `${S}[data-playing='true']:hover .${VC}--play,` +
            `${S}[data-playing='true']:focus-within .${VC}--play{opacity:1}`,
        // A fade that cannot be reversed by a pointer is a control the visitor cannot reach: coarse pointers
        // (a phone) keep the disc visible rather than hiding it behind a hover that will never happen.
        `@media(hover:none){${S}[data-playing='true'] .${VC}--play{opacity:.55}}`,
        `@media(prefers-reduced-motion:reduce){${B}{transition:none}` +
            `${S}[data-playing='true'] .${VC}--play{opacity:1}}`,
        // Phones get a smaller disc — 56px over a 390px-wide band is a lid, not a control.
        `@media(max-width:749px){${B}{--aqx-vc-size:44px}` +
            `${B}.${VC}--sound,${B}.${VC}--fs{--aqx-vc-size:34px}}`,
        // ── the scrub bar ─────────────────────────────────────────────────────────────────────────────────
        `${S}.${VC}-timeline{position:absolute;inset-inline:12px;inset-block-end:10px;z-index:2;` +
            `block-size:14px;display:flex;align-items:center;cursor:pointer;opacity:0;transition:opacity .18s ease}`,
        `${S}:hover .${VC}-timeline,${S}:focus-within .${VC}-timeline{opacity:1}`,
        `@media(hover:none){${S}.${VC}-timeline{opacity:1}}`,
        `${S}.${VC}-timeline__track{position:relative;inline-size:100%;block-size:4px;border-radius:999px;` +
            `background:color-mix(in srgb,var(--aqx-vc-fg,#fff) 30%,transparent);overflow:hidden}`,
        `${S}.${VC}-timeline__bar{display:block;inline-size:0;block-size:100%;border-radius:999px;` +
            `background:var(--aqx-vc-progress,var(--aqx-theme-accent,currentColor))}`,
    ].join('');
}
/* ── the icons ────────────────────────────────────────────────────────────────────────────────────────
 * Drawn on a 24x24 grid, `currentColor`, `aria-hidden` (the BUTTON carries the label — an icon that also
 * announced itself would be read twice). `focusable="false"` is the IE/Edge-legacy guard Shrine ships and
 * costs nothing to keep.
 */
const svgProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    'aria-hidden': true,
    focusable: 'false',
};
function IconPlay({ className = '' }) {
    return (_jsx("svg", { ...svgProps, className: className, children: _jsx("path", { d: "M8.4 4.9a1.4 1.4 0 0 0-2.1 1.2v11.8a1.4 1.4 0 0 0 2.1 1.2l9.7-5.9a1.4 1.4 0 0 0 0-2.4L8.4 4.9Z" }) }));
}
function IconPause({ className = '' }) {
    return (_jsxs("svg", { ...svgProps, className: className, children: [_jsx("rect", { x: "6.5", y: "4.5", width: "4", height: "15", rx: "1.4" }), _jsx("rect", { x: "13.5", y: "4.5", width: "4", height: "15", rx: "1.4" })] }));
}
function IconSoundOff({ className = '' }) {
    return (_jsxs("svg", { ...svgProps, className: className, children: [_jsx("path", { d: "M11.2 4.2 6.9 7.6H4a1 1 0 0 0-1 1v6.8a1 1 0 0 0 1 1h2.9l4.3 3.4a.9.9 0 0 0 1.5-.7V4.9a.9.9 0 0 0-1.5-.7Z" }), _jsx("path", { d: "M21.1 9.5a1 1 0 0 0-1.4-1.4L17.6 10l-2.1-2.1a1 1 0 0 0-1.4 1.4l2.1 2.1-2.1 2.1a1 1 0 1 0 1.4 1.4l2.1-2.1 2.1 2.1a1 1 0 0 0 1.4-1.4L19 11.4l2.1-1.9Z" })] }));
}
function IconSoundOn({ className = '' }) {
    return (_jsxs("svg", { ...svgProps, className: className, children: [_jsx("path", { d: "M11.2 4.2 6.9 7.6H4a1 1 0 0 0-1 1v6.8a1 1 0 0 0 1 1h2.9l4.3 3.4a.9.9 0 0 0 1.5-.7V4.9a.9.9 0 0 0-1.5-.7Z" }), _jsx("path", { d: "M15.6 8.1a1 1 0 0 0-1.3 1.5 3.2 3.2 0 0 1 0 4.8 1 1 0 0 0 1.3 1.5 5.2 5.2 0 0 0 0-7.8Z" }), _jsx("path", { d: "M18.2 5.1a1 1 0 1 0-1.2 1.6 6.8 6.8 0 0 1 0 10.6 1 1 0 0 0 1.2 1.6 8.8 8.8 0 0 0 0-13.8Z" })] }));
}
function IconFullscreen({ className = '' }) {
    return (_jsx("svg", { ...svgProps, className: className, children: _jsx("path", { d: "M4 9.2a1 1 0 0 0 1-1V5.9h2.3a1 1 0 1 0 0-2H4.6a1.6 1.6 0 0 0-1.6 1.6v2.7a1 1 0 0 0 1 1Zm12.7-3.3H19v2.3a1 1 0 1 0 2 0V5.5a1.6 1.6 0 0 0-1.6-1.6h-2.7a1 1 0 1 0 0 2ZM20 14.8a1 1 0 0 0-1 1v2.3h-2.3a1 1 0 1 0 0 2h2.7a1.6 1.6 0 0 0 1.6-1.6v-2.7a1 1 0 0 0-1-1Zm-12.7 3.3H5v-2.3a1 1 0 1 0-2 0v2.7a1.6 1.6 0 0 0 1.6 1.6h2.7a1 1 0 1 0 0-2Z" }) }));
}
/**
 * The icons on their own, for a DECORATIVE marker — the slider's poster-only slide (a video block with no
 * file uploaded yet) stamps a play triangle on the still to say "this is a video slot". It is `aria-hidden`
 * and drives nothing, so it is an icon and not a button; it ships as SVG for the same reason the buttons
 * do, since a `▶` typed into the markup is painted by whatever font the visitor happens to have.
 */
export { IconPlay as VideoIconPlay, IconSoundOff as VideoIconSoundOff, IconFullscreen as VideoIconFullscreen, };
/** The placement class, or nothing when the section positions the control itself. */
const place = (p) => (p === 'custom' ? '' : ` ${VC}--${p}`);
/**
 * The play/pause disc.
 *
 * It renders BOTH icons and both labels; `videoInit` never rewrites them, it only flips `data-playing` on
 * the container. `aria-label` starts on "play" because that is what the SSR icon shows — the engine
 * rewrites it on the very first `play` event, before a screen reader can reach a running clip.
 */
export function VideoPlayButton({ labels, placement = 'center', className, style, }) {
    return (_jsxs("button", { type: "button", "data-aqx-video-play": "", "data-label-play": labels.play, "data-label-pause": labels.pause, "aria-label": labels.play, className: `${VC} ${VC}--play${place(placement)}${className ? ` ${className}` : ''}`, style: style, children: [_jsx(IconPlay, { className: `${VC}__icon ${VC}__icon--play` }), _jsx(IconPause, { className: `${VC}__icon ${VC}__icon--pause` })] }));
}
/** The sound toggle. `aria-pressed` is kept in step by the engine. */
export function VideoSoundButton({ labels, placement = 'bottom-end', className, style, }) {
    return (_jsxs("button", { type: "button", "data-aqx-video-sound": "", "data-label-mute": labels.mute, "data-label-unmute": labels.unmute, "aria-label": labels.unmute, "aria-pressed": false, className: `${VC} ${VC}--sound${place(placement)}${className ? ` ${className}` : ''}`, style: style, children: [_jsx(IconSoundOff, { className: `${VC}__icon ${VC}__icon--muted` }), _jsx(IconSoundOn, { className: `${VC}__icon ${VC}__icon--unmuted` })] }));
}
/** Fullscreen. */
export function VideoFullscreenButton({ labels, placement = 'bottom-start', className, style, }) {
    return (_jsx("button", { type: "button", "data-aqx-video-fs": "", "aria-label": labels.fullscreen, className: `${VC} ${VC}--fs${place(placement)}${className ? ` ${className}` : ''}`, style: style, children: _jsx(IconFullscreen, { className: `${VC}__icon ${VC}__icon--fs` }) }));
}
/** The scrub bar (`videoInit` fills the inner bar and seeks on click). */
export function VideoTimeline({ className }) {
    return (_jsx("div", { "data-aqx-video-timeline": "", className: `${VC}-timeline${className ? ` ${className}` : ''}`, children: _jsx("span", { className: `${VC}-timeline__track`, children: _jsx("span", { "data-aqx-video-progress": "", className: `${VC}-timeline__bar` }) }) }));
}
/**
 * A clip the VISITOR starts, wearing our chrome instead of the browser's.
 *
 * Three sections offer "muted autoplay: off" — the editorial media of `results`, a column of
 * `custom-columns`, the media block of `icons-with-content`. Turning it off used to mean `controls`: the
 * browser's own grey bar, in the browser's own styling, across the bottom of the seller's picture. It is
 * the one piece of a themed storefront that no theme could reach, and on the same page as a Video section
 * wearing this chrome it simply looked like a mistake.
 *
 * `data-mode="click"` also makes a click anywhere on the frame start the clip (the engine does that, not
 * this markup), so the visitor does not have to hit the disc. `preload="metadata"` is kept from the
 * markup it replaces: a real `src` is required — the visitor may press play before any observer fires —
 * and metadata is a few KB against the whole file.
 */
export function ClickToPlayVideo({ src, poster, labels, style, videoStyle, children, }) {
    return (_jsxs("div", { "data-aqx-video": "", "data-mode": "click", "data-aqx-video-inline": "", style: { position: 'relative', display: 'block', ...style }, children: [_jsx("video", { "data-aqx-video-el": "", src: src, poster: poster, preload: "metadata", playsInline: true, style: { inlineSize: '100%', display: 'block', ...videoStyle }, children: children }), _jsx(VideoPlayButton, { labels: labels }), _jsx(VideoSoundButton, { labels: labels }), _jsx(VideoTimeline, {})] }));
}
/**
 * Les attributs qui font d'un élément LE CONTENEUR du moteur (`videoInit`). Écrits en un seul endroit
 * parce qu'ils voyagent : selon que la vidéo porte une commande ou non, ils sont posés sur la `<video>`
 * elle-même ou sur le cadre qui l'enveloppe. Voir {@link VideoFrame}.
 */
export function videoContainerAttrs(mode, inline = false) {
    return {
        'data-aqx-video': '',
        'data-mode': mode,
        ...(inline ? { 'data-aqx-video-inline': '' } : null),
    };
}
/**
 * LE CADRE QUI DONNE UN TOIT AU DISQUE — et qui n'existe que quand la vendeuse l'a demandé.
 *
 * Une douzaine de sections écrivent leur clip en « conteneur de lui-même » : `data-aqx-video` posé
 * DIRECTEMENT sur la `<video>`, précisément pour n'introduire aucun div entre la tuile et son média
 * (les commentaires de `custom-columns`, `image-with-text`, `multirow` le disent chacun à leur tour).
 * C'est une bonne décision de mise en page, et c'est aussi la raison pour laquelle ces clips n'ont
 * jamais pu porter la moindre commande : une `<video>` ne peut pas avoir d'enfants, donc le bouton
 * n'avait nulle part où aller.
 *
 * Ce composant tranche sans rien casser : `on={false}` ne rend RIEN de plus — la section garde le
 * balisage d'aujourd'hui, à l'octet près, et une boutique en ligne ne bouge pas. `on={true}` enveloppe
 * le clip dans un cadre positionné qui devient le conteneur du moteur, et y pose le disque.
 *
 * L'appelant reste responsable d'une chose : poser {@link videoContainerAttrs} sur la `<video>` quand
 * `on` est faux, et ne PAS les poser quand il est vrai (deux conteneurs imbriqués feraient jouer le
 * moteur deux fois sur le même fichier).
 *
 * `inline` VAUT VRAI PAR DÉFAUT, et c'est la correction d'un défaut trouvé au navigateur le 19/09/2026.
 * `initVideos` classe les clips en deux familles : les BANDES, qui jouent de leur propre chef, et les
 * DIAPOSITIVES, qui se partagent un seul créneau de lecture. Une `<video>` conteneur d'elle-même est
 * une bande PAR DÉFINITION (`isInline` teste exactement cela) — donc l'envelopper dans un div muet la
 * faisait basculer en diapositive : sur deux colonnes côte à côte, une seule jouait encore, et c'était
 * l'autre. Le cadre hérite donc du statut qu'il remplace.
 */
export function VideoFrame({ on, labels, mode = 'autoplay', inline = true, className, style, children, }) {
    if (!on)
        return _jsx(_Fragment, { children: children });
    return (_jsxs("div", { className: className, ...videoContainerAttrs(mode, inline), style: { position: 'relative', display: 'block', ...style }, children: [children, _jsx(VideoPlayButton, { labels: labels })] }));
}
//# sourceMappingURL=videoControls.js.map